import { useRecoilState, useRecoilValue } from 'recoil'
import styled from 'styled-components'

import EventInfoSearchCircle from '~/features/event-info/components/EventInfoSearchCircle'
import EventListDropdown from '~/features/event-info/components/EventListDropdown'
import PageTabs from '~/features/event-info/components/PageTabs'
import useEventTab from '~/features/event-info/hooks/useEventTab'
import {
  useEventInfoList,
  useEventInfoMain,
} from '~/features/event-info/queries/event-info'
import { EventTabs } from '~/features/event-info/types/event-info'
import EventInfoIntroduction from '~/pages/event-info/EventInfoIntroduction'
import EventInfoImage from '~/shared/assets/background/event-info.png'
import EventInfoImage2 from '~/shared/assets/background/event-info-2.png'
import EventAnnouncementImage from '~/shared/assets/images/event-announcement.png'
import LoadingScreen from '~/shared/components/loading/LoadingScreen'
import NoData from '~/shared/components/NoData'
import TopSection from '~/shared/components/TopSection'
import {
  eventListDropdownOptionsState,
  selectedEventIdState,
} from '~/shared/states'
import theme from '~/shared/styles/theme'

function EventInfoPage() {
  const [selectedEventId, setSelectedEventId] =
    useRecoilState(selectedEventIdState)
  const dropdownOptions = useRecoilValue(eventListDropdownOptionsState)

  const { eventTab, setEventTab } = useEventTab()

  const { isLoading: isEventListLoading } = useEventInfoList()

  const { data, isLoading: isEventInfoLoading } =
    useEventInfoMain(selectedEventId)

  const handleDropdownChange = (
    event: React.ChangeEvent<HTMLSelectElement>,
  ) => {
    setSelectedEventId(Number(event.target.value))
  }

  if (isEventListLoading || isEventInfoLoading) return <LoadingScreen />

  return (
    <Wrapper>
      <Container>
        <TopSection title={data!.title} description={data!.desc} />
        <PageTabs
          tabContents={[
            EventTabs.INTRODUCTION,
            EventTabs.TIMETABLE,
            EventTabs.LAYOUT,
            EventTabs.CIRCLE_LIST,
          ]}
          selectedTab={eventTab}
          $size="md"
          setSelectTab={setEventTab}
        />
        <EventListDropdown
          options={dropdownOptions}
          onChange={handleDropdownChange}
          value={selectedEventId}
        />
        <AnnouncementBox>
          <sub>{data?.image_title}</sub>
          <h2>{data?.image_desc}</h2>
        </AnnouncementBox>
        {eventTab === EventTabs.INTRODUCTION ? (
          <EventInfoIntroduction />
        ) : eventTab === EventTabs.CIRCLE_LIST ? (
          <EventInfoSearchCircle selectedEventId={selectedEventId} />
        ) : (
          <NoData />
        )}
      </Container>
    </Wrapper>
  )
}

export default EventInfoPage

const Wrapper = styled.div`
  position: relative;

  display: flex;
  flex-direction: column;
  gap: 60px;
  align-items: center;

  min-height: 200vh;
  margin-top: ${theme.size.headerHeight}px;

  white-space: pre-wrap;

  background-image: url(${EventInfoImage});
  background-repeat: no-repeat;
  background-size: 100vw auto;

  &::before {
    content: '';

    position: absolute;
    z-index: -1;
    top: 1050px;
    left: 0;

    width: 100%;
    height: 100vh;

    background-image: url(${EventInfoImage2});
    background-repeat: no-repeat;
    background-size: 100vw auto;
  }

  @media ${theme.device.large} {
    gap: 48px;
    padding: 0 60px;
  }

  @media ${theme.device.medium} {
    gap: 24px;
    min-height: auto;
    margin-top: ${theme.size.mobileHeaderHeight}px;
    padding: 0 20px;

    &::before {
      top: 700;
      height: auto;
    }
  }
`

const Container = styled.div`
  display: flex;
  flex-direction: column;
  gap: 36px;

  width: 100%;
  max-width: 1080px;
  height: 100%;
`

const AnnouncementBox = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  align-items: center;
  justify-content: center;

  height: 255px;

  font-family: NanumSquareNeo-Bold, sans-serif;
  font-weight: bold;
  color: white;

  background-image: url(${EventAnnouncementImage});
  border-radius: 12px;

  sub {
    font-size: 18px;

    @media ${theme.device.medium} {
      font-size: 18px;
    }
  }

  h2 {
    font-size: 42px;

    @media ${theme.device.medium} {
      font-size: 42px;
      text-align: center;
    }
  }
`
